import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { 
  Settings, MessageSquare, BarChart3, TestTube2, Plus, Edit, Trash2, 
  CheckCircle, XCircle, Phone, Send, Eye, RotateCcw, Clock, Loader2
} from 'lucide-react';

interface SmsConfiguration {
  id: number;
  settingKey: string;
  settingValue: string;
  description: string;
  isActive: boolean;
  updatedAt: string;
}

interface SmsTemplate {
  id: number;
  name: string;
  templateKey: string;
  messageContent: string;
  description: string;
  variables: string[];
  category: string;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
}

interface SmsLog {
  id: number;
  recipient: string;
  message: string;
  templateId: number | null;
  templateName: string | null;
  templateKey: string | null;
  messageId: string | null;
  status: string;
  errorMessage: string | null;
  reference: string | null;
  sentAt: string;
  deliveredAt: string | null;
  sentBy: number | null;
}

interface SmsBalance {
  balance: number;
  currency: string;
  lastUpdated: string;
}

interface SmsAnalytics {
  totalSms: number;
  last30Days: number;
  last7Days: number;
  statusBreakdown: { status: string; count: number; }[];
  deliveryRate: number;
  lastUpdated: string;
}

interface OtpAnalyticsUser {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  phone: string;
  totalOtpRequests: number;
  verifiedRequests: number;
  isPhoneVerified: boolean;
  systemPhoneVerified: boolean;
  phoneVerifiedAt: string | null;
  lastOtpRequestAt: string;
  firstOtpRequestAt: string;
  createdAt: string | null;
}

interface OtpAnalytics {
  summary: {
    totalUsers: number;
    verifiedUsers: number;
    unverifiedUsers: number;
    totalOtpRequests: number;
    averageRequestsPerUser: string;
  };
  analytics: OtpAnalyticsUser[];
  lastUpdated: string;
}

// Balance Display Component
function BalanceDisplay() {
  const { data: balance, isLoading } = useQuery<SmsBalance>({
    queryKey: ['/api/sms-config/balance'],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2">
        <span className="text-5xl font-bold" data-testid="text-balance">
          {balance?.balance?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
        </span>
        <span className="text-lg text-muted-foreground">Credits</span>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>Last updated: {balance?.lastUpdated ? new Date(balance.lastUpdated).toLocaleString('en-ZA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'Never'}</span>
      </div>

      {balance && balance.balance < 50 && (
        <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
            ⚠️ Low Balance Warning
          </p>
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            Your SMS credit balance is running low. Please top up to avoid service interruption.
          </p>
        </div>
      )}

      {balance && balance.balance >= 50 && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            ✓ Balance Healthy
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Your SMS credit balance is sufficient for normal operations.
          </p>
        </div>
      )}
    </div>
  );
}

export default function SMSConfigurationTab() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('settings');
  const [editingSetting, setEditingSetting] = useState<SmsConfiguration | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<SmsTemplate | null>(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isSettingDialogOpen, setIsSettingDialogOpen] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testTemplateId, setTestTemplateId] = useState<number | null>(null);
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  
  // Reporting tab filters
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('all');
  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const [reportPage, setReportPage] = useState(1);

  // OTP Analytics filters and pagination
  const [otpUserType, setOtpUserType] = useState<'existing' | 'new'>('existing');
  const [existingUsersPage, setExistingUsersPage] = useState(1);
  const [newUsersPage, setNewUsersPage] = useState(1);
  const [existingUsersDateFrom, setExistingUsersDateFrom] = useState('');
  const [existingUsersDateTo, setExistingUsersDateTo] = useState('');
  const [newUsersDateFrom, setNewUsersDateFrom] = useState('');
  const [newUsersDateTo, setNewUsersDateTo] = useState('');
  const ITEMS_PER_PAGE = 20;

  // SMS Factory API connection / credentials status
  const {
    data: smsCredentials,
    isLoading: smsCredentialsLoading,
    refetch: refetchSmsCredentials,
  } = useQuery<{ valid: boolean }>({
    queryKey: ['/api/sms/validate-credentials'],
    queryFn: async () => {
      const response = await apiRequest('POST', '/api/sms/validate-credentials');
      return response.json();
    },
  });

  // Fetch SMS settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['/api/sms-config/settings'],
  });

  // Fetch SMS templates
  const { data: templates, isLoading: templatesLoading } = useQuery<SmsTemplate[]>({
    queryKey: ['/api/sms-config/templates'],
  });

  // Fetch SMS logs with filters for reporting (admin-only endpoint with auth)
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['/api/sms-config/logs', reportPage, reportStatusFilter, reportSearchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: reportPage.toString(),
        limit: '20'
      });
      if (reportStatusFilter !== 'all') {
        params.append('status', reportStatusFilter);
      }
      if (reportSearchQuery) {
        params.append('search', reportSearchQuery);
      }

      const response = await apiRequest('GET', `/api/sms-config/logs?${params.toString()}`);
      return response.json();
    }
  });

  // Fetch analytics (used in Analytics tab; Reporting uses logsData.metrics)
  const { data: analytics } = useQuery<SmsAnalytics>({
    queryKey: ['/api/sms-config/analytics'],
  });

  // Fetch OTP analytics
  const { data: otpAnalytics, isLoading: otpAnalyticsLoading } = useQuery<OtpAnalytics>({
    queryKey: ['/api/admin/otp-analytics'],
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: async (data: { key: string; settingValue: string; description: string; isActive: boolean }) => {
      return apiRequest('PUT', `/api/sms-config/settings/${data.key}`, { body: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms-config/settings'] });
      toast({ title: 'Success', description: 'Setting updated successfully' });
      setIsSettingDialogOpen(false);
      setEditingSetting(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (data: Partial<SmsTemplate>) => {
      return apiRequest('POST', '/api/sms-config/templates', { body: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms-config/templates'] });
      toast({ title: 'Success', description: 'Template created successfully' });
      setIsTemplateDialogOpen(false);
      setEditingTemplate(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async (data: Partial<SmsTemplate> & { id: number }) => {
      return apiRequest('PUT', `/api/sms-config/templates/${data.id}`, { body: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms-config/templates'] });
      toast({ title: 'Success', description: 'Template updated successfully' });
      setIsTemplateDialogOpen(false);
      setEditingTemplate(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest('DELETE', `/api/sms-config/templates/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms-config/templates'] });
      toast({ title: 'Success', description: 'Template deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Test template mutation
  const testTemplateMutation = useMutation({
    mutationFn: async (data: { templateId: number; phoneNumber: string; variables: Record<string, string> }) => {
      const response = await apiRequest('POST', '/api/sms-config/test', { body: data });
      return response.json();
    },
    onSuccess: (data) => {
      toast({ 
        title: 'Template Preview', 
        description: `Message: ${data.preview}\nLength: ${data.length} chars (${data.segments} segments)` 
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const handleSaveSetting = () => {
    if (!editingSetting) return;
    updateSettingMutation.mutate({
      key: editingSetting.settingKey,
      settingValue: editingSetting.settingValue,
      description: editingSetting.description,
      isActive: editingSetting.isActive
    });
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate) return;
    
    if (editingTemplate.id) {
      updateTemplateMutation.mutate(editingTemplate);
    } else {
      createTemplateMutation.mutate(editingTemplate);
    }
  };

  const handleTestTemplate = () => {
    if (!testTemplateId || !testPhoneNumber) {
      toast({ 
        title: 'Validation Error', 
        description: 'Please select a template and enter a phone number',
        variant: 'destructive' 
      });
      return;
    }

    testTemplateMutation.mutate({
      templateId: testTemplateId,
      phoneNumber: testPhoneNumber,
      variables: testVariables
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" data-testid="heading-sms-config">SMS & OTP Configuration</h2>
          <p className="text-muted-foreground">Manage SMS settings, templates, and monitor delivery</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2" data-testid="tab-templates">
            <MessageSquare className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="reporting" className="flex items-center gap-2" data-testid="tab-reporting">
            <BarChart3 className="h-4 w-4" />
            Reporting
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="testing" className="flex items-center gap-2" data-testid="tab-testing">
            <TestTube2 className="h-4 w-4" />
            Testing
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          {/* SMS Factory API Connection Status */}
          <Card>
            <CardHeader>
              <CardTitle>SMS Factory API Status</CardTitle>
              <CardDescription>Verify that the SMS Factory API is reachable and credentials are valid</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {smsCredentialsLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-muted-foreground">Checking API connection...</span>
                    </>
                  ) : smsCredentials?.valid ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-green-600">
                        SMS Factory API is connected and authenticated
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="text-red-600">
                        SMS Factory API connection failed or credentials are invalid
                      </span>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchSmsCredentials()}
                  disabled={smsCredentialsLoading}
                >
                  {smsCredentialsLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    "Test API Connection"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SMS Configuration Settings</CardTitle>
              <CardDescription>Configure global SMS and OTP settings</CardDescription>
            </CardHeader>
            <CardContent>
              {settingsLoading ? (
                <div>Loading settings...</div>
              ) : (
                <div className="space-y-4">
                  {(settings as SmsConfiguration[] | undefined)?.map((setting: SmsConfiguration) => (
                    <div key={setting.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium" data-testid={`text-setting-${setting.settingKey}`}>{setting.settingKey}</h4>
                          {setting.isActive ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{setting.description}</p>
                        <p className="text-sm font-mono mt-1" data-testid={`text-value-${setting.settingKey}`}>{setting.settingValue}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setEditingSetting(setting);
                          setIsSettingDialogOpen(true);
                        }}
                        data-testid={`button-edit-setting-${setting.settingKey}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => {
                setEditingTemplate({
                  id: 0,
                  name: '',
                  templateKey: '',
                  messageContent: '',
                  description: '',
                  variables: [],
                  category: 'custom',
                  isActive: true,
                  usageCount: 0,
                  createdAt: ''
                });
                setIsTemplateDialogOpen(true);
              }}
              data-testid="button-create-template"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>

          <div className="grid gap-4">
            {templatesLoading ? (
              <div>Loading templates...</div>
            ) : (
              templates?.map((template) => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {template.name}
                          <Badge variant={template.isActive ? "default" : "secondary"} data-testid={`badge-template-${template.id}`}>
                            {template.category}
                          </Badge>
                        </CardTitle>
                        <CardDescription>{template.description}</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(template);
                            setIsTemplateDialogOpen(true);
                          }}
                          data-testid={`button-edit-template-${template.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteTemplateMutation.mutate(template.id)}
                          data-testid={`button-delete-template-${template.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-mono bg-muted p-3 rounded" data-testid={`text-template-content-${template.id}`}>
                        {template.messageContent}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Variables: {template.variables?.join(', ') || 'None'}</span>
                        <span>Used {template.usageCount} times</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Reporting Tab */}
        <TabsContent value="reporting" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>SMS Delivery Audit & Reporting</CardTitle>
              <CardDescription>Monitor and track SMS delivery status with detailed logs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search by phone number..."
                    value={reportSearchQuery}
                    onChange={(e) => {
                      setReportSearchQuery(e.target.value);
                      setReportPage(1);
                    }}
                    data-testid="input-report-search"
                  />
                </div>
                <Select 
                  value={reportStatusFilter} 
                  onValueChange={(value) => {
                    setReportStatusFilter(value);
                    setReportPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]" data-testid="select-report-status">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Summary Stats */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total SMS</p>
                  <p className="text-2xl font-bold" data-testid="text-total-sms">
                    {logsData?.pagination?.total ?? 0}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Delivered</p>
                  <p className="text-2xl font-bold text-green-600">
                    {logsData?.metrics?.delivered ??
                      logsData?.logs?.filter(
                        (log: SmsLog) => log.status === 'delivered' || !!log.deliveredAt
                      ).length ??
                      0}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-950 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {logsData?.metrics?.pending ??
                      logsData?.logs?.filter(
                        (log: SmsLog) =>
                          !log.deliveredAt && (log.status === 'pending' || log.status === 'sent')
                      ).length ??
                      0}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-950 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-red-600">
                    {logsData?.metrics?.failed ??
                      logsData?.logs?.filter((log: SmsLog) => log.status === 'failed').length ??
                      0}
                  </p>
                </div>
              </div>

              {/* Logs Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Message Preview</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">
                          Loading delivery logs...
                        </TableCell>
                      </TableRow>
                    ) : logsData?.logs?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No SMS delivery records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      logsData?.logs?.map((log: SmsLog) => (
                        <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                          <TableCell className="font-mono text-sm">
                            {new Date(log.sentAt).toLocaleString('en-ZA', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-mono" data-testid={`text-recipient-${log.id}`}>
                            {log.recipient}
                          </TableCell>
                          <TableCell>
                            {log.templateName ? (
                              <div>
                                <p className="font-medium text-sm">{log.templateName}</p>
                                <p className="text-xs text-muted-foreground">{log.templateKey}</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">Custom</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={log.message}>
                            {log.message?.substring(0, 50)}{log.message?.length > 50 ? '...' : ''}
                          </TableCell>
                          <TableCell>
                            {(log.status === 'delivered' || log.deliveredAt) && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Delivered
                              </Badge>
                            )}
                            {log.status === 'sent' && (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                <Send className="h-3 w-3 mr-1" />
                                Sent
                              </Badge>
                            )}
                            {log.status === 'failed' && (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                            {log.status === 'pending' && (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-xs space-y-1">
                              {log.messageId && (
                                <p className="font-mono text-muted-foreground">ID: {log.messageId.substring(0, 12)}...</p>
                              )}
                              {log.errorMessage && (
                                <p className="text-red-600" title={log.errorMessage}>
                                  Error: {log.errorMessage.substring(0, 30)}...
                                </p>
                              )}
                              {log.deliveredAt && (
                                <p className="text-green-600">
                                  ✓ {new Date(log.deliveredAt).toLocaleTimeString('en-ZA')}
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {logsData?.pagination && logsData.pagination.pages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {logsData.pagination.page} of {logsData.pagination.pages} 
                    ({logsData.pagination.total} total records)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={reportPage === 1}
                      onClick={() => setReportPage(p => p - 1)}
                      data-testid="button-prev-page"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={reportPage >= logsData.pagination.pages}
                      onClick={() => setReportPage(p => p + 1)}
                      data-testid="button-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Credit Balance Card - Most Prominent */}
          <Card className="border-2 border-primary">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  SMS Credit Balance
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/sms-config/balance'] })}
                  data-testid="button-refresh-balance"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <BalanceDisplay />
            </CardContent>
          </Card>

          {/* OTP Verification Analytics */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>OTP Verification Analytics</CardTitle>
                  <CardDescription>User phone verification details and OTP request history</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/otp-analytics'] })}
                  data-testid="button-refresh-otp-analytics"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {otpAnalyticsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : otpAnalytics ? (
                (() => {
                  // Get today's start time
                  const todayStart = new Date();
                  todayStart.setHours(0, 0, 0, 0);

                  // Split users into existing (before today) and new (today onwards)
                  const existingUsers = otpAnalytics.analytics.filter(user => {
                    if (!user.createdAt) return true; // Default to existing if no createdAt
                    const userCreatedDate = new Date(user.createdAt);
                    return userCreatedDate < todayStart;
                  });

                  const newUsers = otpAnalytics.analytics.filter(user => {
                    if (!user.createdAt) return false;
                    const userCreatedDate = new Date(user.createdAt);
                    return userCreatedDate >= todayStart;
                  });

                  // Apply date filters for existing users
                  let filteredExistingUsers = existingUsers;
                  if (existingUsersDateFrom) {
                    filteredExistingUsers = filteredExistingUsers.filter(user => {
                      if (!user.createdAt) return true;
                      return new Date(user.createdAt) >= new Date(existingUsersDateFrom);
                    });
                  }
                  if (existingUsersDateTo) {
                    filteredExistingUsers = filteredExistingUsers.filter(user => {
                      if (!user.createdAt) return true;
                      const toDate = new Date(existingUsersDateTo);
                      toDate.setHours(23, 59, 59, 999);
                      return new Date(user.createdAt) <= toDate;
                    });
                  }

                  // Apply date filters for new users
                  let filteredNewUsers = newUsers;
                  if (newUsersDateFrom) {
                    filteredNewUsers = filteredNewUsers.filter(user => {
                      if (!user.createdAt) return false;
                      return new Date(user.createdAt) >= new Date(newUsersDateFrom);
                    });
                  }
                  if (newUsersDateTo) {
                    filteredNewUsers = filteredNewUsers.filter(user => {
                      if (!user.createdAt) return false;
                      const toDate = new Date(newUsersDateTo);
                      toDate.setHours(23, 59, 59, 999);
                      return new Date(user.createdAt) <= toDate;
                    });
                  }

                  // Pagination for existing users
                  const existingUsersStartIndex = (existingUsersPage - 1) * ITEMS_PER_PAGE;
                  const existingUsersEndIndex = existingUsersStartIndex + ITEMS_PER_PAGE;
                  const paginatedExistingUsers = filteredExistingUsers.slice(existingUsersStartIndex, existingUsersEndIndex);
                  const existingUsersTotalPages = Math.ceil(filteredExistingUsers.length / ITEMS_PER_PAGE);

                  // Pagination for new users
                  const newUsersStartIndex = (newUsersPage - 1) * ITEMS_PER_PAGE;
                  const newUsersEndIndex = newUsersStartIndex + ITEMS_PER_PAGE;
                  const paginatedNewUsers = filteredNewUsers.slice(newUsersStartIndex, newUsersEndIndex);
                  const newUsersTotalPages = Math.ceil(filteredNewUsers.length / ITEMS_PER_PAGE);

                  // Calculate summary stats based on active tab
                  const activeUsers = otpUserType === 'existing' ? filteredExistingUsers : filteredNewUsers;
                  const activeTotalUsers = activeUsers.length;
                  const activeVerifiedUsers = activeUsers.filter(u => u.isPhoneVerified).length;
                  const activeTotalOtpRequests = activeUsers.reduce((sum, u) => sum + u.totalOtpRequests, 0);
                  const activeAvgRequests = activeTotalUsers > 0 ? (activeTotalOtpRequests / activeTotalUsers).toFixed(2) : '0';

                  const renderUserTable = (users: OtpAnalyticsUser[]) => (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Phone Number</TableHead>
                            <TableHead>Account Created</TableHead>
                            <TableHead>OTP Requests</TableHead>
                            <TableHead>Verification Status</TableHead>
                            <TableHead>Last Request</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                No users found for this category
                              </TableCell>
                            </TableRow>
                          ) : (
                            users.map((user) => (
                              <TableRow key={`${user.userId}-${user.phone}`} data-testid={`row-otp-user-${user.userId}`}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium" data-testid={`text-user-name-${user.userId}`}>
                                      {user.firstName} {user.lastName}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                    <p className="text-xs text-muted-foreground">@{user.username || 'N/A'}</p>
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono" data-testid={`text-phone-${user.userId}`}>
                                  {user.phone}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-ZA', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-semibold" data-testid={`text-requests-${user.userId}`}>
                                      {user.totalOtpRequests}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {user.verifiedRequests} verified
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {user.isPhoneVerified ? (
                                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" data-testid={`badge-verified-${user.userId}`}>
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Verified
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" data-testid={`badge-unverified-${user.userId}`}>
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Not Verified
                                    </Badge>
                                  )}
                                  {user.phoneVerifiedAt && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {new Date(user.phoneVerifiedAt).toLocaleDateString('en-ZA', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  <div>
                                    <p data-testid={`text-last-request-${user.userId}`}>
                                      {new Date(user.lastOtpRequestAt).toLocaleDateString('en-ZA', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      First: {new Date(user.firstOtpRequestAt).toLocaleDateString('en-ZA', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </p>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  );

                  return (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid gap-4 md:grid-cols-4">
                        <div className="p-4 border rounded-lg">
                          <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                          <p className="text-2xl font-bold mt-1" data-testid="text-otp-total-users">
                            {activeTotalUsers}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {otpUserType === 'existing' ? 'Before today' : 'Today onwards'}
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950">
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">Verified Users</p>
                          <p className="text-2xl font-bold mt-1 text-green-600" data-testid="text-otp-verified-users">
                            {activeVerifiedUsers}
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg bg-orange-50 dark:bg-orange-950">
                          <p className="text-sm font-medium text-orange-800 dark:text-orange-200">Unverified Users</p>
                          <p className="text-2xl font-bold mt-1 text-orange-600" data-testid="text-otp-unverified-users">
                            {activeTotalUsers - activeVerifiedUsers}
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <p className="text-sm font-medium text-muted-foreground">Total OTP Requests</p>
                          <p className="text-2xl font-bold mt-1" data-testid="text-otp-total-requests">
                            {activeTotalOtpRequests}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Avg: {activeAvgRequests} per user
                          </p>
                        </div>
                      </div>

                      {/* Nested Tabs for User Types */}
                      <Tabs value={otpUserType} onValueChange={(val) => setOtpUserType(val as 'existing' | 'new')}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="existing" data-testid="tab-existing-users">
                            Existing Users ({existingUsers.length})
                          </TabsTrigger>
                          <TabsTrigger value="new" data-testid="tab-new-users">
                            New Users ({newUsers.length})
                          </TabsTrigger>
                        </TabsList>

                        {/* Existing Users Tab */}
                        <TabsContent value="existing" className="space-y-4">
                          {/* Date Filters */}
                          <div className="flex gap-4 items-end">
                            <div className="flex-1">
                              <Label htmlFor="existing-date-from">From Date</Label>
                              <Input
                                id="existing-date-from"
                                type="date"
                                value={existingUsersDateFrom}
                                onChange={(e) => {
                                  setExistingUsersDateFrom(e.target.value);
                                  setExistingUsersPage(1);
                                }}
                                data-testid="input-existing-date-from"
                              />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor="existing-date-to">To Date</Label>
                              <Input
                                id="existing-date-to"
                                type="date"
                                value={existingUsersDateTo}
                                onChange={(e) => {
                                  setExistingUsersDateTo(e.target.value);
                                  setExistingUsersPage(1);
                                }}
                                data-testid="input-existing-date-to"
                              />
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setExistingUsersDateFrom('');
                                setExistingUsersDateTo('');
                                setExistingUsersPage(1);
                              }}
                              data-testid="button-clear-existing-filters"
                            >
                              Clear Filters
                            </Button>
                          </div>

                          {renderUserTable(paginatedExistingUsers)}

                          {/* Pagination */}
                          {existingUsersTotalPages > 1 && (
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-muted-foreground">
                                Showing {existingUsersStartIndex + 1}-{Math.min(existingUsersEndIndex, filteredExistingUsers.length)} of {filteredExistingUsers.length}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setExistingUsersPage(p => Math.max(1, p - 1))}
                                  disabled={existingUsersPage === 1}
                                  data-testid="button-existing-prev"
                                >
                                  Previous
                                </Button>
                                <div className="flex items-center gap-2 text-sm">
                                  Page {existingUsersPage} of {existingUsersTotalPages}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setExistingUsersPage(p => Math.min(existingUsersTotalPages, p + 1))}
                                  disabled={existingUsersPage === existingUsersTotalPages}
                                  data-testid="button-existing-next"
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          )}
                        </TabsContent>

                        {/* New Users Tab */}
                        <TabsContent value="new" className="space-y-4">
                          {/* Date Filters */}
                          <div className="flex gap-4 items-end">
                            <div className="flex-1">
                              <Label htmlFor="new-date-from">From Date</Label>
                              <Input
                                id="new-date-from"
                                type="date"
                                value={newUsersDateFrom}
                                onChange={(e) => {
                                  setNewUsersDateFrom(e.target.value);
                                  setNewUsersPage(1);
                                }}
                                data-testid="input-new-date-from"
                              />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor="new-date-to">To Date</Label>
                              <Input
                                id="new-date-to"
                                type="date"
                                value={newUsersDateTo}
                                onChange={(e) => {
                                  setNewUsersDateTo(e.target.value);
                                  setNewUsersPage(1);
                                }}
                                data-testid="input-new-date-to"
                              />
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setNewUsersDateFrom('');
                                setNewUsersDateTo('');
                                setNewUsersPage(1);
                              }}
                              data-testid="button-clear-new-filters"
                            >
                              Clear Filters
                            </Button>
                          </div>

                          {renderUserTable(paginatedNewUsers)}

                          {/* Pagination */}
                          {newUsersTotalPages > 1 && (
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-muted-foreground">
                                Showing {newUsersStartIndex + 1}-{Math.min(newUsersEndIndex, filteredNewUsers.length)} of {filteredNewUsers.length}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setNewUsersPage(p => Math.max(1, p - 1))}
                                  disabled={newUsersPage === 1}
                                  data-testid="button-new-prev"
                                >
                                  Previous
                                </Button>
                                <div className="flex items-center gap-2 text-sm">
                                  Page {newUsersPage} of {newUsersTotalPages}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setNewUsersPage(p => Math.min(newUsersTotalPages, p + 1))}
                                  disabled={newUsersPage === newUsersTotalPages}
                                  data-testid="button-new-next"
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>
                    </div>
                  );
                })()
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Failed to load OTP analytics
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testing Tab */}
        <TabsContent value="testing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube2 className="h-5 w-5" />
                Test SMS Template
              </CardTitle>
              <CardDescription>Preview and test SMS templates before sending</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Template</Label>
                <Select 
                  value={testTemplateId?.toString()} 
                  onValueChange={(val) => setTestTemplateId(parseInt(val))}
                >
                  <SelectTrigger data-testid="select-test-template">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates?.map((template) => (
                      <SelectItem key={template.id} value={template.id.toString()}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="27821234567"
                  value={testPhoneNumber}
                  onChange={(e) => setTestPhoneNumber(e.target.value)}
                  data-testid="input-test-phone"
                />
              </div>

              {testTemplateId && templates?.find(t => t.id === testTemplateId)?.variables && (
                <div className="space-y-2">
                  <Label>Template Variables</Label>
                  {templates.find(t => t.id === testTemplateId)?.variables?.map((variable) => (
                    <Input
                      key={variable}
                      placeholder={`Enter value for {{${variable}}}`}
                      value={testVariables[variable] || ''}
                      onChange={(e) => setTestVariables({ ...testVariables, [variable]: e.target.value })}
                      data-testid={`input-variable-${variable}`}
                    />
                  ))}
                </div>
              )}

              <Button 
                onClick={handleTestTemplate} 
                className="w-full"
                disabled={testTemplateMutation.isPending}
                data-testid="button-test-template"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview Template
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Setting Dialog */}
      <Dialog open={isSettingDialogOpen} onOpenChange={setIsSettingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Setting</DialogTitle>
            <DialogDescription>Update SMS configuration setting</DialogDescription>
          </DialogHeader>
          {editingSetting && (
            <div className="space-y-4">
              <div>
                <Label>Setting Key</Label>
                <Input value={editingSetting.settingKey} disabled />
              </div>
              <div>
                <Label>Value</Label>
                <Input
                  value={editingSetting.settingValue}
                  onChange={(e) => setEditingSetting({ ...editingSetting, settingValue: e.target.value })}
                  data-testid="input-edit-setting-value"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingSetting.description}
                  onChange={(e) => setEditingSetting({ ...editingSetting, description: e.target.value })}
                  data-testid="textarea-edit-setting-description"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingSetting.isActive}
                  onChange={(e) => setEditingSetting({ ...editingSetting, isActive: e.target.checked })}
                  data-testid="checkbox-edit-setting-active"
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSetting} disabled={updateSettingMutation.isPending} data-testid="button-save-setting">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.id ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>Configure SMS message template</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  data-testid="input-template-name"
                />
              </div>
              <div>
                <Label>Template Key</Label>
                <Input
                  value={editingTemplate.templateKey}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, templateKey: e.target.value })}
                  placeholder="e.g., otp_verification"
                  data-testid="input-template-key"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={editingTemplate.category}
                  onValueChange={(val) => setEditingTemplate({ ...editingTemplate, category: val })}
                >
                  <SelectTrigger data-testid="select-template-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="otp">OTP</SelectItem>
                    <SelectItem value="competition">Competition</SelectItem>
                    <SelectItem value="winner">Winner</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Message Content</Label>
                <Textarea
                  value={editingTemplate.messageContent}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, messageContent: e.target.value })}
                  placeholder="Use {{variable}} for dynamic content"
                  rows={4}
                  data-testid="textarea-template-content"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {editingTemplate.messageContent.length} characters
                </p>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  data-testid="input-template-description"
                />
              </div>
              <div>
                <Label>Variables (comma-separated)</Label>
                <Input
                  value={editingTemplate.variables?.join(', ') || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, variables: e.target.value.split(',').map(v => v.trim()) })}
                  placeholder="e.g., code, name, expiry"
                  data-testid="input-template-variables"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTemplate.isActive}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, isActive: e.target.checked })}
                  data-testid="checkbox-template-active"
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveTemplate} 
              disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
              data-testid="button-save-template"
            >
              {editingTemplate?.id ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
